import classNames from 'classnames';
import getConfig from 'next/config';
import Link from 'next/link';
import React, { memo } from 'react';

import { Badge, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { META_DISCLAIMER } from '../../../../constants/common';
import { LIST_BY_TAGS } from '../../../../constants/routes';
import { sendViewCommentEvent } from '../../../../helpers/analytics';
import { checkTriggerWords, getMaterialLink } from '../../../../helpers/news';
import { useDictionaries } from '../../../../hooks/news';
import { useViewPlatform } from '../../../../hooks/useViewPlatform';
import { isServer } from '../../../../utils/isServer';
import { createLinkProps } from '../../../../utils/routing';
import { CommentsButton } from '../../../CommentsSection/commentsButton';
import { Share } from '../../../Share';
import { ShareClickHandler } from '../../../Share/types';
import { IArticleProps } from '../Article';
import { PartnerLabel } from '../ArticleHeader/partnerLabel';
import { LikeArticleButton } from '../LikeArticleButton';

import styles from './article-footer.module.scss';

export interface IArticleHeaderProps extends Pick<IArticleProps, 'material'> {
  commentsCount?: number;
  commentsRef: React.RefObject<HTMLDivElement>;
  onToggleLike: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  isArticleLiked: boolean;
  likesCount: number;
  handleShowAuthPopup: () => void;
  isUserLogged: boolean;
  onShareClick?: ShareClickHandler;
}

export const ArticleFooter = memo<IArticleHeaderProps>(
  ({
    material,
    commentsCount,
    commentsRef,
    onToggleLike,
    isArticleLiked,
    likesCount,
    handleShowAuthPopup,
    isUserLogged,
    onShareClick,
  }) => {
    const { tags, productTags } = useDictionaries();

    const isPartnerNews = material.rubric === 'novosti-partnerov';

    const isMobile = useIsMobile();
    const platformOs = useViewPlatform();

    const hasTriggerWords =
      checkTriggerWords(material.title) ||
      checkTriggerWords(material.htmlBody) ||
      checkTriggerWords(material.lead) ||
      checkTriggerWords(material.authorName);

    let getShareUrl = material.externalLink;
    if (!material.externalLink) {
      const { webPath } = getConfig().publicRuntimeConfig;
      const { as } = getMaterialLink(material);

      const formattedLocation = isServer ? '' : window.location.search.replace('?wl=true', '');
      getShareUrl = `${webPath}${as}${formattedLocation}`;
    }

    const renderTags = material.tags
      .reduce((output, item) => {
        const tag = tags?.find((elem) => elem.name === item) || productTags.find((elem) => elem.name === item);
        if (tag) output.push(tag);
        return output;
      }, [])
      .map((item) => {
        const { as, href } = createLinkProps('list', LIST_BY_TAGS, {
          tag: item.alias,
        });
        return (
          <Link href={href} as={as}>
            <Badge
              key={`tag-${item.alias}`}
              className={classNames(styles.tag, 'h-mt-8')}
              text={
                <a className={styles.link} href={as}>
                  {item.name}
                </a>
              }
            />
          </Link>
        );
      });

    const renderPartnerLabel = !isPartnerNews ? null : <PartnerLabel organizationData={material.organizationData} />;

    const multipleTags = material.tags.length >= 2;

    const hasComments = commentsCount || material.canCreateNewComments;

    const renderCombinationKeysOs = () => {
      switch (platformOs) {
        case 'Windows':
          return 'Ctrl+Enter';
        case 'Mac':
          return '⌘+Enter';
        default:
          return 'Ctrl+Enter';
      }
    };

    return (
      <div className={styles.wrapper}>
        {hasTriggerWords && (
          <Typography.Text size={12} className={classNames('h-mb-16', 'h-color-D60')}>
            {META_DISCLAIMER}
          </Typography.Text>
        )}
        {!isMobile && (
          <Typography.Text size={12} className={classNames('h-color-D60', 'h-mb-20')}>
            Если вы заметили опечатку или неточность в статье, выделите её и нажмите {renderCombinationKeysOs()}
          </Typography.Text>
        )}
        <div
          className={classNames(styles.top, {
            [styles.topMultipleTags]: multipleTags,
            [styles.topCommentsAndMultipleTags]: multipleTags && hasComments,
          })}
        >
          <div
            className={classNames(styles.tagsWrapper, {
              [styles.multipleTags]: multipleTags,
              [styles.multipleTagsWithComments]: multipleTags && hasComments,
            })}
          >
            {isPartnerNews ? renderPartnerLabel : renderTags}
          </div>
          <div className={styles.topBlock}>
            <Share shareUrl={getShareUrl} title={material.title} onShareClick={onShareClick} />
            <LikeArticleButton onToggleLike={onToggleLike} isLiked={isArticleLiked} likesCount={likesCount} />
            <CommentsButton
              commentsCount={commentsCount}
              commentsRef={commentsRef}
              canCreateNewComments={material.canCreateNewComments}
              handleShowAuthPopup={handleShowAuthPopup}
              isUserLogged={isUserLogged}
              onClick={sendViewCommentEvent}
            />
          </div>
        </div>
      </div>
    );
  },
);
